// GoalAuthorizationSection — per-goal write policy builder (Phase B, U-3)
// Lets the goal owner choose how autonomously the agent may act:
//   read_only          — a derived write is surfaced, never applied
//   approve_each        — every write queued to the Approvals inbox (default)
//   auto_within_bounds  — in-bounds writes auto-apply; owner/admin only
// The backend is the source of truth for RBAC (PUT .../authorization 403s for
// non-elevated roles) — this form does not try to duplicate that check, it
// just surfaces whatever error the backend returns.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  setGoalAuthorization, listWriteTools,
  type Goal, type AuthorizationMode,
} from '../../api/goals';
import { useCanWriteAgent } from '../../hooks/useCanWriteAgent';
import styles from './GoalAuthorizationSection.module.css';

const MODE_OPTIONS: Array<{ value: AuthorizationMode; label: string; hint: string }> = [
  { value: 'read_only', label: 'Read-only', hint: 'Never writes — a drafted action is surfaced but never applied.' },
  { value: 'approve_each', label: 'Approve every write', hint: 'Safe default — every write waits for your decision in Approvals.' },
  { value: 'auto_within_bounds', label: 'Auto-apply within limits', hint: 'Owner/admin only — writes that fit the bounds below apply automatically.' },
];

function CreditUsageBar({ used, ceiling }: { used: number; ceiling: number | null }) {
  if (ceiling == null) {
    return (
      <p className={styles.usageNote}>
        {used} credit{used !== 1 ? 's' : ''} used this month · no monthly ceiling set
      </p>
    );
  }
  const pct = ceiling > 0 ? Math.min(100, Math.round((used / ceiling) * 100)) : 100;
  const atCeiling = used >= ceiling;
  return (
    <div className={styles.usage}>
      <div className={styles.usageLabelRow}>
        <span>{used} / {ceiling} credits this month</span>
        {atCeiling && <span className={styles.usageAtCeiling}>At ceiling</span>}
      </div>
      <div className={styles.usageTrack}>
        <div
          className={`${styles.usageFill} ${atCeiling ? styles.usageFillOver : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function GoalAuthorizationSection({ goal, onUpdated }: { goal: Goal; onUpdated: () => void }) {
  const canWrite = useCanWriteAgent();
  const policy = goal.authorization_policy;
  const [mode, setMode] = useState<AuthorizationMode>(policy.mode);
  const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set(policy.allowed_tools));
  const [ceilingPerRun, setCeilingPerRun] = useState<string>(
    policy.credit_ceiling_per_run != null ? String(policy.credit_ceiling_per_run) : '',
  );
  const [ceilingPerMonth, setCeilingPerMonth] = useState<string>(
    policy.credit_ceiling_per_month != null ? String(policy.credit_ceiling_per_month) : '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const { data: writeTools } = useQuery({
    queryKey: ['write-tools'],
    queryFn: listWriteTools,
    staleTime: 10 * 60_000,
  });

  function toggleTool(tool: string) {
    setSelectedTools(prev => {
      const next = new Set(prev);
      if (next.has(tool)) next.delete(tool); else next.add(tool);
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await setGoalAuthorization(goal.id, {
        mode,
        allowed_tools: Array.from(selectedTools),
        credit_ceiling_per_run: ceilingPerRun.trim() ? Number(ceilingPerRun) : null,
        credit_ceiling_per_month: ceilingPerMonth.trim() ? Number(ceilingPerMonth) : null,
      });
      setSaved(true);
      onUpdated();
    } catch (e) {
      setError((e as Error).message || 'Failed to update authorization policy.');
    } finally {
      setSaving(false);
    }
  }

  const dirty =
    mode !== policy.mode ||
    ceilingPerRun !== (policy.credit_ceiling_per_run != null ? String(policy.credit_ceiling_per_run) : '') ||
    ceilingPerMonth !== (policy.credit_ceiling_per_month != null ? String(policy.credit_ceiling_per_month) : '') ||
    selectedTools.size !== policy.allowed_tools.length ||
    Array.from(selectedTools).some(t => !policy.allowed_tools.includes(t));

  return (
    <div className={styles.container}>
      <CreditUsageBar used={goal.credits_used_this_month ?? 0} ceiling={policy.credit_ceiling_per_month} />

      <div className={styles.modeList}>
        {MODE_OPTIONS.map(opt => (
          <label key={opt.value} className={`${styles.modeOption} ${mode === opt.value ? styles.modeOptionActive : ''}`}>
            <input
              type="radio"
              name="authorization-mode"
              value={opt.value}
              checked={mode === opt.value}
              disabled={!canWrite}
              onChange={() => { setMode(opt.value); setSaved(false); }}
            />
            <div className={styles.modeOptionBody}>
              <span className={styles.modeOptionLabel}>{opt.label}</span>
              <span className={styles.modeOptionHint}>{opt.hint}</span>
            </div>
          </label>
        ))}
      </div>

      {mode === 'auto_within_bounds' && (
        <div className={styles.bounds}>
          <div className={styles.ceilingRow}>
            <label className={styles.ceilingField}>
              <span className={styles.ceilingLabel}>Credit ceiling / run</span>
              <input
                type="number" min={0} placeholder="Unlimited"
                className={styles.ceilingInput}
                value={ceilingPerRun}
                disabled={!canWrite}
                onChange={e => { setCeilingPerRun(e.target.value); setSaved(false); }}
              />
            </label>
            <label className={styles.ceilingField}>
              <span className={styles.ceilingLabel}>Credit ceiling / month</span>
              <input
                type="number" min={0} placeholder="Unlimited"
                className={styles.ceilingInput}
                value={ceilingPerMonth}
                disabled={!canWrite}
                onChange={e => { setCeilingPerMonth(e.target.value); setSaved(false); }}
              />
            </label>
          </div>

          <p className={styles.toolsLabel}>Allowed write tools (narrows the registry — never widens it)</p>
          <div className={styles.toolsGrid}>
            {(writeTools ?? []).map(tool => (
              <label key={tool} className={styles.toolCheckbox}>
                <input
                  type="checkbox"
                  checked={selectedTools.has(tool)}
                  disabled={!canWrite}
                  onChange={() => { toggleTool(tool); setSaved(false); }}
                />
                {tool}
              </label>
            ))}
            {writeTools && writeTools.length === 0 && (
              <span className={styles.toolsEmpty}>No write tools registered.</span>
            )}
          </div>
        </div>
      )}

      {error && <p className={styles.error}>{error}</p>}
      {saved && !dirty && <p className={styles.success}>Saved.</p>}

      {canWrite ? (
        <button className={styles.saveBtn} onClick={handleSave} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save authorization policy'}
        </button>
      ) : (
        <p className={styles.readOnlyNote}>
          Read-only — ask an owner or admin to change this goal&apos;s autonomy settings.
        </p>
      )}
    </div>
  );
}
